import React, { useEffect, useCallback, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  auctionBidders,
  auctionGet,
  setActiveView,
} from "../../../Redux/Slices/auctionSlice";

import Styles from "./RAMainScreen.module.css";

import AddIcon from "@mui/icons-material/Add";
import SelfListingSkeleton from "../../../commons/AvailableListingSkeleton";
import RATender from "../RATender/RATender";
import { Add } from "@mui/icons-material";
import emptyAvailableListing from "../../../assets/images/emptyAvailableListing.svg";
import { useLocation } from "react-router-dom";
import CreateRATender from "../CreateRaTender/CreateRaTender";

// 1. CONFIGURATION: Extract static data outside component to prevent re-creation
const NOTIFICATION_TRIGGERS = [
  "BSRANotifyInvitedGenerator",
  "BSRANotifyConsumerTenderCreated",
  "BSRANotifyConsumerTenderTerminated",
  "BSRANotifyConsumerTenderUpdate",
  "BSRANotifyConsumerBidCreated",
  "BSRANotifyBidWinnerGenerator",
  "BSRANotifyBidRejectedGenerator",
  "BSRANotifyAmountChangeRequestGenerator",
  "BSRANotifyAmountChangeResponseConsumer",
  "BSRANotifyConsumerBidUpdated",
  "BSRANotifyBidWinnerGeneratorStageTwo",
  "BSRANotifyBidWinnerGeneratorStageOne",
  "BSRANotifyBidRejectedGeneratorStageOne",
  "BSRANotifyBidRejectedGeneratorStageTwo",
  "BSRANotifyGeneratorBidCreated",
  "BSRANotifyConsumerTenderActivation",
  "BSRANotifyGeneratorBidUpdated",
  "BSRANotifyConsumerTenderStart",
  "BSRANotifyConsumerTenderEnd",
  "BSRANotifyGeneratorTenderStart",
  "BSRANotifyGeneratorTenderEnd",
  "BSRANotifyConsumerBidStart",
  "BSRANotifyConsumerBidEnd",
  "BSRANotifyGeneratorBidStart",
  "BSRANotifyGeneratorBidEnd",
  "BSRANotifyConsumerStartAuction",
  "BSRANotifyConsumerEndAuction",
  "BSRANotifyGeneratorStartAuction",
  "BSRANotifyGeneratorEndAuction",
  "BSRANotifyTenderActivationAllGenerator",
  "BSRANotifyAmountChangeRequestConsumer",
  "BSRANotifyAmountChangeResponseGenerator",
  "BSRANotifyBidWinnerConsumer",
  "BSRAEmdBlockedNotification",
  "BSRAEmdReleasedNotification",
  "BSRAConsumerRaFeeBlockedNotification",
  "BSRAGenerateConsent",
  "BSRFQNotifyConsumerTenderCreated",
  "BSRANotifyLOIUploaded",
  "BSRAAgreementUploaded",
  "BSRAScheduleGenerate",
  "BSRANotifyMakerChecker",
];

const VIEW_FILTERS = [
  {
    name: "All",
    value: "ALL",
  },
  {
    name: "Drafted",
    value: "Drafted",
  },
  {
    name: "IPO",
    value: "IPO",
  },
  {
    name: "Live RA",
    value: "live_ra",
  },
  {
    name: "Documentation",
    value: "TERMS",
  },
  {
    name: "Ongoing Contract",
    value: "CLOSED",
  },
];

const RAMainScreen = () => {
  const dispatch = useDispatch();
  const auctionList = useSelector((state) => state?.auctionSlice?.auctionGet);
  const role = useSelector((state) =>
    (state?.persistedReducer?.user?.activeRole || "").toLowerCase(),
  );
  const loacation = useLocation();

  const checkIfinitiate =
    new URLSearchParams(loacation.search).get("type") === "initiate";

  const viewType = null;

  const notify = useSelector((state) => state?.notificationSlice?.latest);
  const activeState = useSelector((state) => state?.auctionSlice?.activeView);
  const totalPages = useSelector(
    (state) => state?.auctionSlice?.auctionTotalPages,
  );

  const [page, setPage] = useState(0);
  const [expandedTender, setExpandendTender] = useState("");
  const [createModal, setCreateModal] = useState(false);
  const listContainerRef = useRef(null);

  // 3. LOGIC: Helper to get the correct API section parameter
  const getApiSection = useCallback((viewState) => {
    return VIEW_FILTERS.find((f) => f.name === viewState)?.value || "All";
  }, []);

  const fetchAuctionData = useCallback((options = {}) => {
    const payload = {
      section: getApiSection(activeState),
      tenderType: "RA",
      page,
      auctionType: viewType && `auctionType=${viewType}`,
      silent: options.silent,
    };

    if (checkIfinitiate) {
      dispatch(auctionGet(payload));
    } else {
      dispatch(auctionBidders(payload));
    }
  }, [
    activeState,
    role,
    page,
    dispatch,
    getApiSection,
    viewType,
    checkIfinitiate,
  ]);

  // Re-fetch on activeState, role, or page change
  useEffect(() => {
    if (!activeState) {
      dispatch(setActiveView("All"));
      return;
    }
    fetchAuctionData();
  }, [activeState, role, page, dispatch, fetchAuctionData, viewType]);

  // Notification listener
  useEffect(() => {
    if (!notify?.key || !NOTIFICATION_TRIGGERS.includes(notify.key)) return;

    if (role === "consumer") {
      fetchAuctionData({ silent: true });
    } else {
      setTimeout(() => fetchAuctionData({ silent: true }), 2000);
    }
  }, [notify, fetchAuctionData, role]);

  const handleExpandedTender = (val) => {
    setExpandendTender(val);
  };

  const ContainerHeader = () => {
    return (
      <>
        {createModal && (
          <CreateRATender
            open={createModal}
            handleClose={() => setCreateModal(false)}
            duplicate={null}
            editData={null}
            type={"create"}
            priceDiscoveryType={"RA"}
            onSuccess={() =>
              listContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
            }
          />
        )}
        <div className={Styles.containerHeader}>
          <div className={Styles.containerHeaderLeftCont}>
            {checkIfinitiate ? (
              <>
                <div className={Styles.tenderHeading}>My Tenders</div>
                <div
                  className={Styles.addTenderButton}
                  onClick={() => setCreateModal(true)}
                >
                  <AddIcon style={{ height: "16px", width: "16px" }} />
                  <span>Add Tender</span>
                </div>
              </>
            ) : (
              <>
                <div className={Styles.tenderHeading}>
                  Tender List: <span>{auctionList?.length || 0}</span>
                </div>
              </>
            )}
          </div>
          <div className={Styles.filterSectionMain}>
            {VIEW_FILTERS?.filter(
              (ele) => !(role === "generator" && ele.value === "Drafted"),
            ).map((ele) => (
              <div
                key={ele.value}
                className={
                  activeState === ele.name
                    ? Styles.filterButtonActive
                    : Styles.filterButtonInactive
                }
                onClick={() => {
                  setPage(0);
                  dispatch(setActiveView(ele.name));
                }}
              >
                {ele?.name}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  const PaginationContainer = () => {
    const isPrevDisabled = page === 0;
    const isNextDisabled = page >= totalPages - 1;

    return (
      <div className={Styles.paginationContainer}>
        <div
          className={
            isPrevDisabled
              ? Styles.paginationButtonDisabled
              : Styles.filterButtonInactive
          }
          onClick={isPrevDisabled ? undefined : () => setPage((p) => p - 1)}
        >
          Prev
        </div>

        <div
          className={
            isNextDisabled
              ? Styles.paginationButtonDisabled
              : Styles.filterButtonInactive
          }
          onClick={isNextDisabled ? undefined : () => setPage((p) => p + 1)}
        >
          Next
        </div>
      </div>
    );
  };

  const DisplayListContainer = () => {
    if (auctionList === null) {
      return (
        <div className={Styles.displayTenderContainer}>
          <SelfListingSkeleton count={4} />
        </div>
      );
    }

    if (auctionList.length === 0) {
      return (
        <div className={Styles.emptyState}>
          <img src={emptyAvailableListing} alt="No Tender" />
          <div style={{ color: "var(--primary-color-text)" }}>
            {checkIfinitiate ? (
              <span>
                You have not listed your <b>requirements</b>
              </span>
            ) : (
              <span>
                No Active <b>requirements</b>
              </span>
            )}
          </div>
          {checkIfinitiate && (
            <div
              className={`activeButton_type_2 ${Styles.addButton}`}
              onClick={() => setCreateModal(true)}
            >
              <Add style={{ fontSize: "16px" }} />
              <span>Add Tender</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        ref={listContainerRef}
        className={`scroll_bar_y ${Styles.displayTenderContainer}`}
      >
        {auctionList.map((tender) => (
          <RATender
            key={tender.id}
            data={tender}
            expandedTender={expandedTender}
            handleExpandedTender={handleExpandedTender}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={Styles.rfpMainContainer}>
      {ContainerHeader()}
      {DisplayListContainer()}
      {PaginationContainer()}
    </div>
  );
};

export default RAMainScreen;
